import { GetQueryParams, GetQueryResponse } from '../../../types/api.type';
import { Gallery, GalleryImage } from '../../../types/website.types';
import { sfcApi } from '../../api';

const appendGalleryPictures = (formData: FormData, pictures: GalleryImage[]) => {
  pictures.forEach((picture, index) => {
    formData.append(`image_metadata[${index}][id]`, String(picture.id ?? -1));
    if (picture.destroy) {
      formData.append(`image_metadata[${index}][destroy]`, 'true');
    }

    let image = picture.image;
    if (!image || image.size === 0) {
      image = new File([], 'empty.png');
    }
    formData.append('images', image);
  });
};

const hasPictureChanges = (pictures?: GalleryImage[]) =>
  !!pictures?.some((picture) => picture.destroy || (picture.image && picture.image.size > 0));

export const galleryApi = sfcApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllGalleries: builder.query<GetQueryResponse, GetQueryParams | void>({
      query: (params) => ({
        url: `galleries${params?.scope ? `?scope=${params.scope}` : ''}`,
        method: 'POST',
        body: params?.data
      }),
      providesTags: ['Gallery']
    }),
    getGallery: builder.query<{ gallery: Gallery }, number>({
      query: (id) => `galleries/${id}`,
      providesTags: (result, error, id) => [{ type: 'Gallery', id }]
    }),
    createGallery: builder.mutation<{ gallery: Gallery }, Gallery>({
      query: (data) => {
        const activePictures =
          data.pictures?.filter((picture) => !picture.destroy && picture.image && picture.image.size > 0) ?? [];

        if (activePictures.length === 0) {
          return {
            url: `galleries/create`,
            method: 'POST',
            body: { name: data.name }
          };
        }

        const formData = new FormData();
        formData.append('name', data.name);
        appendGalleryPictures(formData, activePictures);

        return {
          url: `galleries/create`,
          method: 'POST',
          body: formData,
          formData: true
        };
      },
      invalidatesTags: ['Gallery']
    }),
    updateGallery: builder.mutation<{ gallery: Gallery }, Gallery>({
      query: (data) => {
        const { id } = data;
        const pictures = data.pictures ?? [];

        if (!hasPictureChanges(pictures)) {
          return {
            url: `galleries/update/${id}`,
            method: 'PUT',
            body: { name: data.name }
          };
        }

        const formData = new FormData();
        formData.append('name', data.name);
        appendGalleryPictures(formData, pictures);

        return {
          url: `galleries/update/${id}`,
          method: 'PUT',
          body: formData,
          formData: true
        };
      },
      invalidatesTags: (result, error) => [{ type: 'Gallery', id: result?.gallery?.id }, 'Gallery']
    }),
    deleteGallery: builder.mutation<{ gallery: Gallery }, number>({
      query: (id) => ({
        url: `galleries/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['Gallery']
    })
  })
});

export const {
  useGetAllGalleriesQuery,
  useGetGalleryQuery,
  useCreateGalleryMutation,
  useUpdateGalleryMutation,
  useDeleteGalleryMutation
} = galleryApi;
